<script setup lang="ts" generic="T extends Record<string, StatusValue>">
import { computed, ref } from 'vue';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';
import { NxStatusIcon, STATUS_VALUES, type StatusValue } from '../Branding';
import { NxSelectStatusInput } from '../Inputs';
import { defaultEditCellFocusRef, onEditCellKeydown, PartialOrNull } from './internal';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, StatusValue>, 'toCsvString'> & {
      /** value that will be used for the csv export */
      toCsvString?: NxTableColumnProps<T, keyof T, StatusValue>['toCsvString'];
      /**
       * Which statuses the filter / create / edit cells offer. Omitted keeps the
       * sales-document lifecycle (NxSelectStatusInput's default), so existing grids
       * are unaffected; a grid whose column holds a different set passes it, or the
       * dropdowns would offer statuses that column can never hold (I-622).
       */
      statusOptions?: StatusValue[];
      /**
       * Plain-text twin of a status, handed to the three cell inputs. They render
       * every option as an icon, so without it their tooltip and their typeahead
       * fall back to the wire values (`draft`, `open`) — text no reader of this
       * column ever sees, and the kit has no translations of its own. Omitting it
       * is what the dev warning in `useSelectSelection` points at; there was no
       * way to answer it from here before.
       */
      optionText?: (option: StatusValue) => string;
    }
  >(),
  {
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    toCsvString: undefined,
    statusOptions: undefined,
    optionText: undefined,
  }
);

function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }
  return rowData[props.columnId];
}

/**
 * One function, two channels: the cell inputs ask this component for their
 * option text, the filter popover asks the column layer for its labels. Either
 * prop feeds the other, so a column states its words once — otherwise its
 * dropdowns say "Offen" while the filter checklist and its chips say `open`
 * (I-1016, the same contract as NxTableSelectColumn). A column whose two
 * channels genuinely read differently states BOTH; each prop wins in its own
 * channel. Computed rather than resolved at setup: the labels of an ERP grid
 * arrive with its translations, after the column has mounted.
 */
const optionText_ = computed(() => {
  if (props.optionText) return props.optionText;
  const filterLabel = props.filterOptionLabel;
  return filterLabel ? (option: StatusValue) => filterLabel(option) : undefined;
});

const filterOptionLabel_ = computed(() => {
  if (props.filterOptionLabel) return props.filterOptionLabel;
  const optionText = props.optionText;
  return optionText ? (value: string) => optionText(value as StatusValue) : undefined;
});

const filterValue = ref<StatusValue[]>([]);
const createValue = ref<StatusValue[]>([]);
const editValue = ref<StatusValue[]>([]);
/**
 * A saved view hands the quick filter back as plain strings, and the filter
 * cell's select renders its selection through the same `#option` slot as its
 * list — as a status icon. A value the icon cannot draw would show up there as
 * the same empty square the read cell no longer draws (I-1772, review of PR
 * #2027), so only known statuses reach the cell. The saved filter itself is
 * untouched: the table applies it from its own state, not from this ref.
 */
function onQueryLoaded(value: (StatusValue | string)[]) {
  filterValue.value = value.filter((entry): entry is StatusValue =>
    STATUS_VALUES.includes(entry as StatusValue)
  );
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, StatusValue} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'select',
      // Same fallback as NxSelectStatusInput's DOCUMENT_STATUSES default.
      filterOptions: props.filterOptions ??
        props.statusOptions ?? ['draft', 'open', 'accepted', 'declined'],
      filterOptionLabel: filterOptionLabel_,
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span class="one-line">Status</span>
      </slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxSelectStatusInput
          arrow-keys-navigate
          :ref="defaultEditCellFocusRef(registerFocus)"
          v-model="filterValue"
          height="xs"
          :options="props.statusOptions"
          :option-text="optionText_"
          placeholder=""
          :disabled="disabled"
          @update:model-value="
            (value: StatusValue[] | [null]) => {
              setFilterValue(value[0] === null ? [] : [value[0]]);
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'select',
              })
          "
        >
          <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
            <slot name="placeholder" :placeholder="placeholder"></slot>
          </template>
          <template v-if="$slots.option" #option="{ option }">
            <slot name="option" :option="option"></slot>
          </template>
        </NxSelectStatusInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <div style="display: flex">
          <!-- The read cell is where a grid is spent most of the time, and it was
               the one place the labels did not reach: an icon with no words says
               nothing on hover and nothing to a screen reader.
               Drawn only for a value the icon knows (I-1772): the icon hangs glyph
               and colour on `STATUS_VALUES`, so any other cell value left a sized,
               empty, unstyled square in every row — the same hole I-1596 closed in
               the record header. -->
          <NxStatusIcon
            v-if="STATUS_VALUES.includes(rowData[props.columnId])"
            :status="rowData[props.columnId]"
            :title="optionText_?.(rowData[props.columnId])"
            class="icon"
          />
        </div>
      </slot>
    </template>
    <template #createCell="{ setPartialRowData }">
      <slot name="createCell" v-bind="{ setPartialRowData }">
        <NxSelectStatusInput
          arrow-keys-navigate
          v-model="createValue"
          :options="props.statusOptions"
          :option-text="optionText_"
          :placeholder="`${props.displayName ?? (props.columnId as string)}`"
          @update:model-value="
            (value: StatusValue[] | [null]) => {
              setPartialRowData({
                [props.columnId]: value[0],
              } as PartialOrNull<T>);
            }
          "
        >
          <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
            <slot name="placeholder" :placeholder="placeholder"></slot>
          </template>
          <template v-if="$slots.option" #option="{ option }">
            <slot name="option" :option="option"></slot>
          </template>
        </NxSelectStatusInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <!-- TODO: hacky workaround to set the v-model value -->
          {{
            (() => {
              editValue.splice(0, editValue.length, rowData[props.columnId]);
              return null;
            })()
          }}
          <NxSelectStatusInput
            arrow-keys-navigate
            :ref="defaultEditCellFocusRef(registerFocus)"
            v-model="editValue"
            :options="props.statusOptions"
            :option-text="optionText_"
            :placeholder="`${props.displayName ?? (props.columnId as string)}`"
            :value="rowData[props.columnId]"
            @update:model-value="
              (value: StatusValue[] | [null]) => {
                // TODO: what about null
                onEdit({ [props.columnId]: value[0] } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'select',
                })
            "
          >
            <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
              <slot name="placeholder" :placeholder="placeholder"></slot>
            </template>
            <template v-if="$slots.option" #option="{ option }">
              <slot name="option" :option="option"></slot>
            </template>
          </NxSelectStatusInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
.icon {
  margin-inline: auto;
}
</style>
